import floor from 'lodash/floor';

/**
 * Apply a product discount and a coupon to a price, in whole pence.
 *
 * Mirrors how legacy Redux/reselects/cartcalc built the cart total (deleted in
 * order-calc Stage 6.3; the cart now uses holocron order-calc): the product
 * discount is floored to whole pence, then the coupon saving is rounded and
 * subtracted. Rounding the saving rather than the price matters when the result
 * lands on an exact half penny - e.g. 2349p with a 12.5% discount and a 30%
 * coupon gives 2055p then 2055 - 617 = 1438p. Rounding the price instead would
 * give 1439p and disagree with the cart summary and the backend by a penny.
 *
 * Quantity is deliberately not handled here: cartcalc flattens quantity into
 * one entry per unit, so callers must round per unit and multiply afterwards.
 *
 * @param  {Number} pence    price in pence, floored here if not already whole
 * @param  {Number} discount product level discount as a fraction (0-1)
 * @param  {Number} coupon   coupon discount as a fraction (0-1)
 * @return {Number}          discounted price in whole pence
 */
export const couponPencePrice = (pence, discount = 0, coupon = 0) => {
  // Mirrors discountPriceCalc in cartcalc, which floors via convertObjectToInt
  // whether or not there is a discount, so this matches the out.discountPrice the
  // cart works from. Flooring unconditionally also keeps the return value whole
  // when a caller passes a price that is not already in integer pence
  let newPence = (discount > 0 && discount <= 1) ?
    floor(pence * (1 - discount)) :
    floor(pence);

  // Round the saving and subtract it, the way cartcalc builds voucherSaving.
  // Rounding the price instead diverges by a penny on an exact half - do not
  // change this to Math.round(newPence * (1 - coupon))
  if (coupon > 0 && coupon <= 1) {
    newPence -= Math.round(newPence * coupon);
  }

  return newPence;
};

export default couponPencePrice;
